import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Popover, Tag } from 'antd';

interface TagsOverflowProps {
  tags: string[];
}

const TagsOverflow: React.FC<TagsOverflowProps> = ({ tags }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);

  // 监听容器真实宽度
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const marginLeft = parseInt(getComputedStyle(el).marginLeft) + 4 || 0;
      setContainerWidth(el.clientWidth - marginLeft);
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // 计算可见数量
  useLayoutEffect(() => {
    if (!containerRef.current || containerWidth === 0 || tags.length === 0) {
      setVisibleCount(tags.length);
      return;
    }

    const temp = document.createElement('div');
    temp.style.position = 'absolute';
    temp.style.visibility = 'hidden';
    temp.style.whiteSpace = 'nowrap';
    document.body.appendChild(temp);

    tags.forEach((t) => {
      const el = document.createElement('span');
      el.className = 'ant-tag';
      el.style.marginRight = '4px';
      el.textContent = t;
      temp.appendChild(el);
    });

    const children = Array.from(temp.children) as HTMLElement[];
    let totalWidth = 0;
    const SAFE_MARGIN = 12; // 右侧安全边距（防止贴到按钮）
    const MORE_TAG_WIDTH = 60 + SAFE_MARGIN;
    let count = 0;

    for (let i = 0; i < children.length; i++) {
      const w = children[i].offsetWidth;
      if (totalWidth + w + MORE_TAG_WIDTH > containerWidth) break;
      totalWidth += w;
      count++;
    }

    document.body.removeChild(temp);
    setVisibleCount(count);
  }, [tags, containerWidth]);

  const visibleTags = useMemo(
    () => tags.slice(0, visibleCount),
    [tags, visibleCount]
  );
  const hiddenTags = useMemo(
    () => tags.slice(visibleCount),
    [tags, visibleCount]
  );

  return (
    <div ref={containerRef} className="matter-tags-overflow">
      <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        {visibleTags.map((tag, i) => (
          <Tag className="matter-tag" key={i} style={{ margin: 0 }}>
            {tag}
          </Tag>
        ))}

        {hiddenTags.length > 0 && (
          <Popover
            classNames={{ root: 'matter-popover' }}
            placement="bottom"
            content={
              <div>
                {hiddenTags.map((tag, idx) => (
                  <div
                    className="matter_name"
                    key={idx}
                    style={{ lineHeight: '1.5' }}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            }
            title=""
          >
            <Tag color="blue" style={{ cursor: 'pointer', flexShrink: 0 }}>
              +{hiddenTags.length}
            </Tag>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default TagsOverflow;
